import { useSession } from 'next-auth/react';
import useSWR from 'swr';

import { DURATION, NEXT_AUTH_STATUSES } from '@/lib/constants';
import { ShipmentChangeModel } from '@/lib/models/shipment/types';
import { ShipmentHistoryApiResponse } from '@/types/api-types';
import { AsyncDataInterface } from '@/types/context-types';
import { request } from '@/utils/api/request';
import { getShipmentHistoryUrl } from '@/utils/helpers/api-urls';

const ERROR_MESSAGE = 'Error fetching shipment history';

interface UseShipmentHistoryProps {
  shipmentId?: string | null;
}

interface UseShipmentHistory extends AsyncDataInterface {
  shipmentHistory: ShipmentChangeModel[] | null;
}

export const useShipmentHistory = ({ shipmentId }: UseShipmentHistoryProps): UseShipmentHistory => {
  const { status } = useSession();
  const shouldSearch = status === NEXT_AUTH_STATUSES.AUTHENTICATED && shipmentId;

  const { isLoading, error, data } = useSWR<ShipmentHistoryApiResponse>(
    shouldSearch ? getShipmentHistoryUrl(shipmentId) : null,
    request,
    {
      revalidateOnFocus: false,
      errorRetryCount: 2,
      dedupingInterval: DURATION.IN_MILLISECONDS.TEN_MINS,
    },
  );

  const errorMessage = error ? ERROR_MESSAGE : undefined;

  return {
    isLoading,
    shipmentHistory: data?.shipmentHistory || null,
    error: errorMessage,
  };
};
